import { Box, Card, CardContent, Link } from '@mui/material';
import { t } from 'i18next';
import { Severity } from '../../../../common/enums/Severity';
import { UrlHelper } from '../../../../common/utils/UrlHelper';
import { PATHS } from '../../../../routing/routes';
import { ProjectExportOptions } from '../../../options/ProjectExportOptions';
import { Header, HeaderItem } from '../../common/components/Header';
import { HiddenIssues } from '../../common/components/HiddenIssues';
import { Inventory } from '../../common/components/Inventory';
import { BasePrintController, BasePrintControllerProps, BasePrintControllerState } from '../../common/controllers/BasePrintController';
import { IssueGroup } from '../components/IssueGroup';
import { ProjectPrintData } from '../models/ProjectPrintData';
import { ProjectPrintRepository } from '../services/ProjectPrintRepository';

type Props = BasePrintControllerProps & {
  projectId: number;
  options: ProjectExportOptions;
};

type State = BasePrintControllerState & {};

export class ProjectPrintController extends BasePrintController<Props, State> {
  private readonly repository = new ProjectPrintRepository();
  private project: ProjectPrintData | null = null;
  private projectDetails: HeaderItem[] | null = null;

  componentDidMount(): void {
    this.fetchProject();
  }

  componentWillUnmount(): void {
    this.repository.cancelAllRequests();
  }

  render() {
    if (!this.project || !this.projectDetails) return <></>;

    const hasIssues = this.project.hasPriorityIssues();
    const hasHiddenIssues = this.project.hasHiddenIssues();
    const issueGroups = this.project.getIssueGroups();
    const inventory = this.project.getInventory();

    const hasInventory = inventory && !inventory.isEmpty();

    return this.renderBase(
      <Box>
        <Box sx={{ mb: 2 }}>
          <Header title={this.project.getName()} items={this.projectDetails} />
        </Box>
        {hasInventory && (
          <Box sx={{ mb: 2 }}>
            <Inventory inventory={inventory} />
          </Box>
        )}
        {hasIssues && (
          <Card>
            <CardContent>
              <IssueGroup title={t('criticality.high')} issues={issueGroups[Severity.HIGH]} />
              <IssueGroup title={t('criticality.med')} issues={issueGroups[Severity.MED]} />
              <IssueGroup title={t('criticality.low')} issues={issueGroups[Severity.LOW]} />
              <IssueGroup title={t('criticality.passed')} issues={issueGroups[Severity.PASSED]} />
              {hasHiddenIssues && <HiddenIssues />}
            </CardContent>
          </Card>
        )}
        {!hasIssues && hasHiddenIssues && <HiddenIssues section />}
      </Box>
    );
  }

  private async fetchProject() {
    try {
      this.project = await this.repository.getProject(this.props.projectId, this.props.options);

      const description = this.project.getDescription();
      const profile = this.project.getDefaultProfile();
      const lastAnalysis = this.project.getLastAnalysis();
      const orgName = this.project.getOrganizationName();

      this.projectDetails = [{ label: t('organization'), value: orgName }];
      if (description) {
        this.projectDetails.push({ label: t('description'), value: description });
      }
      if (lastAnalysis) {
        this.projectDetails.push({ label: t('lastAnalysis'), value: lastAnalysis.fromNow() });
      } else {
        this.projectDetails.push({ label: t('lastAnalysis'), value: t('noAnalysisYet') });
      }
      if (profile) {
        this.projectDetails.push({
          label: t('defaultProfile'),
          value: (
            <Link key={profile.getId()} href={UrlHelper.generatePath(PATHS.PROFILE_DETAIL, { profileId: profile.getId().toString() })}>
              {profile.getLabel()}
            </Link>
          ),
        });
      }

      this.onRendered();
    } catch (e: unknown) {
      this.onError(e);
    }
  }
}
